import { FileQuestion, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';

import { mensagemDeErro } from '@/lib/erros';
import { formatarDataHora } from '@/lib/formato';
import { juntarPaginas } from '@/lib/paginacao';
import { AnelProgresso } from '@/ui/components/anel-progresso';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { Status } from '@/ui/components/status';
import { TabelaDados } from '@/ui/components/tabela-dados';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { Skeleton } from '@/ui/primitives/skeleton';
import { Migalhas } from '@/ui/shell/migalhas';
import { type Lote, type ResultadoItemLote } from '@fiscoai/contracts';

import { emAndamento, useItensLote, useLote } from './api';
import { colunasItens } from './colunas-itens';
import { ContadoresLote } from './contadores-lote';
import { FORMATO_PLANILHA, STATUS_LOTE } from './rotulos';

function Progresso({ lote }: { lote: Lote }) {
  const percentual = lote.totalItens === 0 ? 0 : (lote.processados / lote.totalItens) * 100;
  const andando = emAndamento(lote.status);
  return (
    <AnelProgresso
      valor={andando ? percentual : 100}
      concluido={!andando}
      rotulo="Progresso do lote"
    >
      <span className="text-xl font-semibold tabular">
        {Math.round(andando ? percentual : 100)}%
      </span>
      <span className="text-xs text-texto-fraco tabular">
        {lote.processados} de {lote.totalItens}
      </span>
    </AnelProgresso>
  );
}

function Cabecalho({ lote }: { lote: Lote | undefined }) {
  if (!lote) return <CabecalhoPagina titulo="Lote" />;
  const status = STATUS_LOTE[lote.status];
  return (
    <CabecalhoPagina
      titulo={lote.formatoPlanilha ? FORMATO_PLANILHA[lote.formatoPlanilha] : 'Lote de arquivos'}
      descricao={`Enviado em ${formatarDataHora(lote.criadoEm)} por ${lote.criadoPor.nome}`}
      acoes={
        <Status tom={status.tom} ativo={emAndamento(lote.status)}>
          {status.rotulo}
        </Status>
      }
    />
  );
}

export function PaginaLote() {
  const { id = '' } = useParams();
  const consulta = useLote(id);
  const [filtro, setFiltro] = useState<ResultadoItemLote>();
  const lote = consulta.data;
  const itens = useItensLote(id, filtro, emAndamento(lote?.status));

  if (consulta.isError) {
    return (
      <EstadoVazio
        tom="perigo"
        icone={<FileQuestion />}
        titulo="Lote não encontrado"
        texto={mensagemDeErro(consulta.error)}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-5">
      <Migalhas itens={[{ rotulo: 'Importar notas', para: '/importacao' }, { rotulo: 'Lote' }]} />
      <Cabecalho lote={lote} />
      {lote ? (
        <div className="grid items-center gap-5 rounded-painel border border-borda bg-superficie p-5 md:grid-cols-[auto_1fr]">
          <Progresso lote={lote} />
          <ContadoresLote lote={lote} filtro={filtro} aoFiltrar={setFiltro} />
        </div>
      ) : (
        <Skeleton className="h-36 rounded-painel" />
      )}
      <TabelaDados
        rotulo="Itens do lote"
        className="min-h-80 flex-1"
        colunas={colunasItens}
        dados={juntarPaginas(itens.data)}
        idLinha={(item) => item.id}
        carregando={itens.isPending}
        aoChegarAoFim={() => {
          if (itens.hasNextPage && !itens.isFetchingNextPage) void itens.fetchNextPage();
        }}
        vazio={
          <EstadoVazio
            icone={<ListChecks />}
            titulo={
              filtro
                ? 'Nenhum item com este resultado'
                : 'Os itens aparecem conforme são processados'
            }
            texto="A lista se atualiza sozinha enquanto o lote roda."
          />
        }
      />
    </div>
  );
}
